import { type Table } from '@tanstack/react-table';
import { Columns3Icon, MoreVerticalIcon, PlusIcon, XIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type {
  MerchantOrderStatusEnum,
  OpsOrder,
} from '@/graphql/generatedTypes';
import {
  DataTableFacetedFilter,
  MULTI_VALUE_SEPARATOR,
} from './TableFacetedFilter';
import {
  ContainerTypeDropdown,
  ContainerTypeComboboxProps,
} from './ContainerTypeDropdown';
import { DateRangeFilter } from '@/components/FilterBar/DateRangeFilter';
import {
  parseDateRangeValue,
  serializeDateRangeValue,
} from '@/views/Order/utils/orderListFilters';
import { useCallback, useMemo, useState } from 'react';
import { Spinner } from '@/components/ui/spinner';
import i18n from '@/utils/i18n';

export interface ToolbarAction {
  label: string;
  icon?: React.ReactNode;
  onClick: (ids: string[]) => Promise<void>;
  variant?: 'default' | 'destructive';
  /** When set, the action is only enabled if every selected row has this status. */
  requiredStatus?: MerchantOrderStatusEnum;
}

export type FilterOptionItem = {
  type?: 'option';
  getLabel: () => string;
  value: string;
};
export type FilterSeparatorItem = { type: 'separator' };
export type FilterTextItem = { type: 'text'; getLabel: () => string };
export type FilterOption =
  | FilterOptionItem
  | FilterSeparatorItem
  | FilterTextItem;

export interface FilterDefinition {
  id: string;
  getLabel: () => string;
  options: FilterOption[];
  /**
   * How the filter is rendered. `select` (the default) picks from `options`;
   * `dateRange` renders a calendar range picker and ignores `options`.
   */
  kind?: 'select' | 'dateRange';
  isDefault?: boolean;
  isRemovable?: boolean;
  /** When true the filter allows selecting multiple values at once. */
  multiSelect?: boolean;
  /** Extra class name forwarded to the popover content element. */
  popoverContentClassName?: string;
  /**
   * When set, the filter loads its `options` via server-side search instead of
   * filtering a preloaded list client-side. The callback receives the current
   * search input; the parent is responsible for fetching and feeding back
   * `options` (and toggling `loading`).
   */
  onSearch?: (query: string) => void;
  /** Loading indicator while async `options` are being fetched. */
  loading?: boolean;
  /** Overrides the popover's empty-state text (e.g. a "type to search" hint). */
  emptyText?: string;
}

export interface ActiveFilter {
  id: string;
  value: string;
}

interface OrderToolbarProps {
  table: Table<OpsOrder>;
  containerTypes: ContainerTypeComboboxProps['containerTypes'];
  selectedContainerTypes: ContainerTypeComboboxProps['containerTypes'];
  onContainerTypeChange: (
    cts: ContainerTypeComboboxProps['containerTypes'],
  ) => void;
  availableFilters: FilterDefinition[];
  activeFilters: ActiveFilter[];
  onFilterChange: (filterId: string, value: string) => void;
  onFilterAdd: (filterId: string) => void;
  onFilterRemove: (filterId: string) => void;
  totalCount: number;
  allSelected: boolean;
  onSelectAll: () => void;
  primaryAction?: ToolbarAction;
  overflowActions?: ToolbarAction[];
  /** Filter IDs to render in the first row alongside the container type combobox. */
  firstRowFilterIds?: string[];
}

export function OrderToolbar({
  table,
  containerTypes,
  selectedContainerTypes,
  onContainerTypeChange,
  availableFilters,
  activeFilters,
  onFilterChange,
  onFilterAdd,
  onFilterRemove,
  totalCount,
  allSelected,
  onSelectAll,
  primaryAction,
  overflowActions,
  firstRowFilterIds,
}: OrderToolbarProps) {
  'use no memo'; // "table" is not compatible with react-compiler
  const selectedRows = table.getSelectedRowModel().rows;
  const pageSelectedCount = selectedRows.length;
  const selectedCount = allSelected ? totalCount : pageSelectedCount;

  const actionIds = useMemo(
    () => (allSelected ? [] : selectedRows.map((r) => r.original.id)),
    [allSelected, selectedRows],
  );

  const [isPrimaryLoading, setIsPrimaryLoading] = useState(false);
  const handlePrimaryAction = useCallback(async () => {
    if (!primaryAction) return;
    setIsPrimaryLoading(true);
    try {
      await primaryAction.onClick(actionIds);
    } finally {
      setIsPrimaryLoading(false);
    }
  }, [primaryAction, actionIds]);

  const primaryStatusMismatch = useMemo(() => {
    if (!primaryAction?.requiredStatus || selectedCount === 0) return false;
    return selectedRows.some(
      (r) => r.original.status !== primaryAction.requiredStatus,
    );
  }, [primaryAction, selectedCount, selectedRows]);

  const primaryDisabled =
    selectedCount === 0 || isPrimaryLoading || primaryStatusMismatch;

  const firstRowFilterIdSet = useMemo(
    () => new Set(firstRowFilterIds ?? []),
    [firstRowFilterIds],
  );

  const firstRowFilters = activeFilters.filter((af) =>
    firstRowFilterIdSet.has(af.id),
  );
  const secondRowFilters = activeFilters.filter(
    (af) => !firstRowFilterIdSet.has(af.id),
  );

  const addableFilters = availableFilters.filter(
    (f) => !activeFilters.some((af) => af.id === f.id),
  );

  const renderFilter = (af: ActiveFilter) => {
    const def = availableFilters.find((f) => f.id === af.id);
    if (!def) return null;
    const isRemovable = def.isRemovable !== false;
    const hasValue = af.value !== '__all__';
    const showX = isRemovable || hasValue;
    const label = def.getLabel();
    return (
      <div key={af.id} className="tw:flex tw:items-center tw:gap-0">
        {def.kind === 'dateRange' ? (
          <DateRangeFilter
            variant="toolbar"
            placeholder={label}
            value={parseDateRangeValue(af.value)}
            onChange={(range) =>
              onFilterChange(af.id, serializeDateRangeValue(range ?? {}))
            }
            className={showX ? 'tw:rounded-r-none' : undefined}
          />
        ) : def.multiSelect ? (
          <DataTableFacetedFilter
            title={label}
            options={def.options}
            multiple
            value={af.value}
            onValueChange={(value) =>
              onFilterChange(
                af.id,
                Array.isArray(value)
                  ? value.join(MULTI_VALUE_SEPARATOR)
                  : value,
              )
            }
            triggerClassName={showX && 'tw:rounded-r-none'}
            popoverContentClassName={def.popoverContentClassName}
            onSearch={def.onSearch}
            loading={def.loading}
            emptyText={def.emptyText}
          />
        ) : (
          <DataTableFacetedFilter
            title={label}
            options={def.options}
            value={af.value}
            onValueChange={(value) => onFilterChange(af.id, value)}
            triggerClassName={showX && 'tw:rounded-r-none'}
            popoverContentClassName={def.popoverContentClassName}
            onSearch={def.onSearch}
            loading={def.loading}
            emptyText={def.emptyText}
          />
        )}
        {showX && (
          <Button
            variant="outline"
            size="icon"
            className="tw:rounded-l-none tw:border-l-0 tw:size-8"
            onClick={() =>
              isRemovable
                ? onFilterRemove(af.id)
                : onFilterChange(af.id, '__all__')
            }
          >
            <XIcon className="tw:size-3" />
            <span className="tw:sr-only">
              {isRemovable ? `Remove ${label} filter` : `Clear ${label} filter`}
            </span>
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-8">
      <div className="tw:flex tw:flex-col tw:gap-4">
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
          {firstRowFilters.map(renderFilter)}
          <ContainerTypeDropdown
            containerTypes={containerTypes}
            selected={selectedContainerTypes}
            onSelect={onContainerTypeChange}
          />
        </div>
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
          {secondRowFilters.map(renderFilter)}
          {addableFilters.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" size="sm">
                  <PlusIcon />
                  {i18n.t('Order:toolbarAddFilter')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {addableFilters.map((f) => (
                  <DropdownMenuItem
                    key={f.id}
                    onClick={() => onFilterAdd(f.id)}
                  >
                    {f.getLabel()}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <Separator />

      <div className="tw:flex tw:items-center tw:justify-between">
        <div className="tw:flex tw:items-center tw:gap-4">
          {selectedCount > 0 && (
            <span className="tw:text-sm tw:font-medium">
              {i18n.t('Order:toolbarSelectionCount', {
                count: selectedCount,
                total: totalCount,
              })}
            </span>
          )}
          {pageSelectedCount > 0 && !allSelected && (
            <Button variant="secondary" size="sm" onClick={onSelectAll}>
              {i18n.t('Order:toolbarSelectAll', { total: totalCount })}
            </Button>
          )}
        </div>
        <div className="tw:flex tw:items-center tw:gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="icon">
                <Columns3Icon />
                <span className="tw:sr-only">Toggle columns</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {table
                .getAllColumns()
                .filter(
                  (column) =>
                    typeof column.accessorFn !== 'undefined' &&
                    column.getCanHide(),
                )
                .map((column) => (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="tw:capitalize"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    {(column.columnDef.meta as { title?: string })?.title ??
                      column.id}
                  </DropdownMenuCheckboxItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {primaryAction && (
            <Tooltip>
              <TooltipTrigger asChild>
                {/* span wrapper so tooltip works on disabled button */}
                <span className="tw:inline-flex">
                  <Button
                    disabled={primaryDisabled}
                    onClick={handlePrimaryAction}
                    variant={
                      primaryAction.variant === 'destructive'
                        ? 'destructive'
                        : 'default'
                    }
                  >
                    {isPrimaryLoading ? <Spinner /> : primaryAction.icon}
                    {primaryAction.label}
                  </Button>
                </span>
              </TooltipTrigger>
              {primaryDisabled && primaryStatusMismatch && (
                <TooltipContent>
                  {i18n.t('Order:toolbarPickingStatusWarning', {
                    status:
                      i18n.t(
                        `MerchantOrder:OrderStatus:${primaryAction.requiredStatus}`,
                      ) ?? primaryAction.requiredStatus,
                  })}
                </TooltipContent>
              )}
            </Tooltip>
          )}

          {overflowActions && overflowActions.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" size="icon">
                  <MoreVerticalIcon />
                  <span className="tw:sr-only">More actions</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {overflowActions.map((action) => (
                  <DropdownMenuItem
                    key={action.label}
                    disabled={selectedCount === 0}
                    onClick={() => action.onClick(actionIds)}
                    variant={action.variant}
                  >
                    {action.icon}
                    {action.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </div>
  );
}
